import { BrandMark } from '@pa/ui'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * The embed's booking overlay (UserFlows P-00; Frontend §4.3 sizing `overlay`), ported from the mockup's
 * `site/Widget.tsx`: drawn inside the full-screen iframe with its own backdrop, so a backdrop click hits frame content
 * and does nothing. The header has the clinic logo (`branding.logoUrl`, else the mark), the name and the Close X, the
 * only way out (no Escape, no backdrop close).
 */
export function EmbedOverlay({
  clinicName,
  logoUrl,
  onClose,
  children,
}: {
  clinicName: string | undefined
  logoUrl: string | null | undefined
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div className="overlay wg-overlay">
      <div className="wg-booking pt pt-embed" role="dialog" aria-modal="true" aria-label="Book an appointment">
        <div className="wg-booking-head">
          <span className="pt-logo">{logoUrl ? <img src={logoUrl} alt="" /> : <BrandMark />}</span>
          <div className="grow">
            {clinicName !== undefined && <b>{clinicName}</b>}
            <span>Online scheduling</span>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" aria-label="Close" onClick={onClose}>
            <X aria-hidden />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/**
 * What the booking flow needs from the embed's host bridge (task 08; `features/frame-bridge` builds it). Every
 * message is a UI event only, never patient data (Security §6.5).
 */
export interface EmbedHost {
  /** Resolves when the visit may start (after `init`, or 5 s without it), with the embed-origin signal. */
  start: () => Promise<string | null>
  /** `step { layout, booked? }`: the panel for entry screens, the overlay from the patient's lookup on. */
  layout: (layout: 'panel' | 'overlay', booked: boolean) => void
  /** The panel or overlay X inside the frame (`close`). */
  requestClose: () => void
  /** The host's `close` (launcher X, `PFAppointments.close()`). Returns the unsubscribe. */
  onHostClose: (listener: () => void) => () => void
  /** `closed`: the visit is ended; the host may remove the iframe. */
  closed: () => void
  /** The panel's root element; its height is reported to the host (`resize`, panel only). */
  panelRef: (el: HTMLElement | null) => void
  /** The direct link (`/c/{slug}?src=…`) that "Continue in a new tab" opens top-level. */
  directLink: string
}
